import { useCallback, useEffect, useReducer, useRef } from 'react';
import {
    getAppointmentKeySlot,
    IAppointmentResult,
    IKeySlot,
    retrieveAvailableLocker,
    SignalrEvent,
    SignalrEnrollment,
    getCheckInFinishedByAdvisor,
    getIsLockerOpen,
} from '../../../../API/GoMoto';
import { useAppointmentState } from '../../../../context/Appointment/appointment';
import { useKioskState } from '../../../../context/Kiosk/kiosk';
import * as signalR from '@microsoft/signalr';
import { ActionMap } from '../../../../API/GoMoto.types';

interface ILockerState {
    complete: boolean;
    lockerNumber: number;
    error: number;
    kioskKey: string;
    id: number | undefined;
    lockerRetrieved: boolean;
    keyStored: boolean;
    lockerClosed: boolean;
}

enum LockerDispatchTypes {
    SetLockerNum = 'LOCKER_SET_NUMBER',
    AdvisorCompleted = 'LOCKER_ADVISOR_COMPLETED',
    UserCompleted = 'LOCKER_USER_COMPLETED',
    AlreadyStored = 'LOCKER_ALREADY_STORED',
    KeyStored = 'LOCKER_KEY_STORED',
    LockerClosed = 'LOCKER_CLOSED'
}

type LockerPayload = {
    [LockerDispatchTypes.SetLockerNum]: IAppointmentResult;
    [LockerDispatchTypes.AdvisorCompleted]: boolean;
    [LockerDispatchTypes.UserCompleted]: boolean;
    [LockerDispatchTypes.AlreadyStored]: IKeySlot | undefined;
    [LockerDispatchTypes.KeyStored]: IKeySlot;
    [LockerDispatchTypes.LockerClosed]: never;
};
type LockerActions = ActionMap<LockerPayload>[keyof ActionMap<LockerPayload>];

/**
 * Waits for the locker to open and close
 *
 * @param lockerNumber the locker number to poll. If one is not passed, an availble
 * one will be retrieved from the server
 */
export const useIsLockerOpComplete = (lockerNumber: number = 0, advisorOp: boolean = false) => {
    const { key, dealer: { key: dealerKey } } = useKioskState();
    const appointment = useAppointmentState();
    const hubConnection = useRef<signalR.HubConnection | undefined>(undefined);
    const retryHubConnectionTimeout = useRef<NodeJS.Timeout | undefined>(undefined);

    const [state, dispatch] = useReducer(
        (state: ILockerState, action: LockerActions) => {
            switch (action.type) {
                case LockerDispatchTypes.SetLockerNum:
                    return {
                        ...state,
                        lockerNumber: action.payload.keySlotNumber,
                        error: action.payload.error,
                        lockerRetrieved: true,
                    };
                case LockerDispatchTypes.AdvisorCompleted:
                    return {
                        ...state,
                        complete: action.payload,
                    };
                case LockerDispatchTypes.UserCompleted:
                    return {
                        ...state,
                        complete: action.payload,
                    };
                case LockerDispatchTypes.LockerClosed:
                    return {
                        ...state,
                        lockerClosed: true,
                    };
                case LockerDispatchTypes.KeyStored:
                    return {
                        ...state,
                        keyStored: true,
                        lockerNumber: action.payload.keySlotNumber,
                        kioskKey: action.payload.kioskKey,
                        id: action.payload.id,
                    };
                case LockerDispatchTypes.AlreadyStored:
                    return {
                        ...state,
                        keyStored: true,
                        lockerRetrieved: true,
                        lockerNumber: action.payload?.keySlotNumber || state.lockerNumber,
                        kioskKey: action.payload?.kioskKey || state.kioskKey,
                        id: action.payload?.id,
                    };
                default:
                    return state;
            }
        },
        {
            complete: false,
            error: 0,
            lockerNumber: lockerNumber,
            kioskKey: key,
            lockerRetrieved: false,
            id: -1,
            lockerClosed: false,
            keyStored: false,
        },
    );

    const subscribeToEvents = useCallback(() => {
        if (hubConnection.current && hubConnection.current.connectionId) {
            if (advisorOp) {
                hubConnection.current.invoke(SignalrEnrollment.ManuallyComplete, appointment.key);
                hubConnection.current.on(SignalrEvent.ManuallyComplete, () => {
                    dispatch({
                        type: LockerDispatchTypes.AdvisorCompleted,
                        payload: true,
                    });
                });
            }
            else {
                hubConnection.current.invoke(SignalrEnrollment.AppointmentKeyStored, appointment.key);
                hubConnection.current.on(SignalrEvent.AppointmentKeyStored, (keySlotData: string) => {
                    const keySlot: IKeySlot | null = JSON.parse(keySlotData);
                    if (keySlot && keySlot.keySlotNumber && keySlot.id && keySlot.kioskKey) {
                        dispatch({
                            type: LockerDispatchTypes.KeyStored,
                            payload: keySlot,
                        });
                    }
                });

                hubConnection.current.invoke(SignalrEnrollment.LockerStatus, appointment.key);
                hubConnection.current.on(SignalrEvent.LockerStatus, (data: string) => {
                    const keySlot: IKeySlot = JSON.parse(data);
                    if (keySlot && keySlot.keySlotNumber && keySlot.kioskKey && !keySlot.isOpen) {
                        dispatch({ type: LockerDispatchTypes.LockerClosed });
                    }
                });
            }
        }
    }, [advisorOp, appointment.key]);

    const startHubConnection = useCallback(async () => {
        retryHubConnectionTimeout.current = undefined;

        if (hubConnection.current) {
            try {
                await hubConnection.current.start();
                console.assert(hubConnection.current.state === signalR.HubConnectionState.Connected);
                subscribeToEvents();
            }
            catch {
                retryHubConnectionTimeout.current = setTimeout(() => startHubConnection(), 5000);
            }
        }
    }, [subscribeToEvents]);

    useEffect(() => {
        if (state.keyStored && state.lockerClosed) {
            dispatch({
                type: LockerDispatchTypes.UserCompleted,
                payload: true,
            });
        }
    }, [state.keyStored, state.lockerClosed]);

    useEffect(() => {
        if (appointment.key && !hubConnection.current) {
            hubConnection.current = new signalR.HubConnectionBuilder()
                .withAutomaticReconnect({
                    nextRetryDelayInMilliseconds: () => 5000,
                })
                .withUrl(process.env.REACT_APP_GOMOTO_API_ROOT + '/signalr', { withCredentials: false })
                .build();

            hubConnection.current.onreconnected(() => {
                subscribeToEvents();

                // Check if locker op was completed while SignalR was disconnected
                if (advisorOp) {
                    getCheckInFinishedByAdvisor(appointment.key)
                        .then((data: boolean) => {
                            dispatch({
                                type: LockerDispatchTypes.AdvisorCompleted,
                                payload: data,
                            });
                        });
                }
                else {
                    getAppointmentKeySlot(appointment.key)
                        .then((data?: IKeySlot) => {
                            if (!!data && data.appointmentKeyStatus === 'slot_assigned') {
                                dispatch({
                                    type: LockerDispatchTypes.KeyStored,
                                    payload: data,
                                });

                                getIsLockerOpen(data.kioskKey, data.keySlotNumber)
                                    .then((isOpen) => {
                                        if (!isOpen) {
                                            dispatch({ type: LockerDispatchTypes.LockerClosed });
                                        }
                                    });
                            }
                        });
                }
            });

            startHubConnection();
        }

        return () => {
            if (retryHubConnectionTimeout.current) {
                clearTimeout(retryHubConnectionTimeout.current);
            }

            if (hubConnection.current) {
                hubConnection.current.off(SignalrEvent.ManuallyComplete);
                hubConnection.current.off(SignalrEvent.LockerStatus);
                hubConnection.current.off(SignalrEvent.AppointmentKeyStored);
                hubConnection.current.stop()
                    .catch((error) => console.log(error));
                hubConnection.current = undefined;
            }
        };
    }, [advisorOp, appointment.key, startHubConnection, subscribeToEvents]);

    useEffect(() => {
        if (!state.lockerRetrieved && !advisorOp) {
            retrieveAvailableLocker(key, dealerKey, appointment.key, lockerNumber)
                .then((result) => {
                    if (result.error === 2) {
                        getAppointmentKeySlot(appointment.key)
                            .then((data?: IKeySlot) => {
                                dispatch({ type: LockerDispatchTypes.AlreadyStored,
                                    payload: data });

                                if (data) {
                                    getIsLockerOpen(data.kioskKey, data.keySlotNumber)
                                        .then((isOpen) => {
                                            if (!isOpen) {
                                                dispatch({ type: LockerDispatchTypes.LockerClosed });
                                            }
                                        });
                                }
                            });
                    }
                    else {
                        dispatch({
                            type: LockerDispatchTypes.SetLockerNum,
                            payload: result,
                        });
                    }
                });
        }
        else if (advisorOp) {
            getCheckInFinishedByAdvisor(appointment.key)
                .then((data: boolean) => {
                    dispatch({
                        type: LockerDispatchTypes.AdvisorCompleted,
                        payload: data,
                    });
                });
        }
    }, [appointment.key, key, state.lockerRetrieved, advisorOp, lockerNumber, dealerKey]);

    return {
        complete: state.complete,
        lockerNumber: state.lockerNumber,
        error: state.error,
        kioskKey: state.kioskKey,
        lockerRetrieved: state.lockerRetrieved,
        id: state.id,
    };
};
